import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useForm, Controller } from "react-hook-form";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { updateDiscoveryState, useDiscoveryState } from "~/lib/discovery/state";
import {
  CoachDemographicsPreferenceSchema,
  UserAndCoachPersonalitySchema,
  coachGenderField,
  coachAgesField,
  userPersonalityField,
  coachPersonalityField,
} from "~/lib/schemas/coach-discovery";

// ---------------------------------------------------------------------------
// Combined schema for section 2
// ---------------------------------------------------------------------------

const Section2Schema = CoachDemographicsPreferenceSchema.merge(
  UserAndCoachPersonalitySchema,
);

type Section2Form = {
  coachGender: string;
  coachAges: string[];
  userPersonality: string[];
  coachPersonality: string[];
};

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/refine/fit")({
  component: RefinePersonalFit,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function RefinePersonalFit() {
  const navigate = useNavigate();
  const { form: discoveryForm } = useDiscoveryState();

  const defaultValues = useMemo(
    (): Section2Form => ({
      coachGender: discoveryForm.coachGender,
      coachAges: discoveryForm.coachAges,
      userPersonality: discoveryForm.userPersonality,
      coachPersonality: discoveryForm.coachPersonality,
    }),
    [discoveryForm],
  );

  const { handleSubmit, control, formState } = useForm<Section2Form>({
    resolver: zodResolver(Section2Schema),
    defaultValues,
    mode: "onTouched",
  });

  const { errors, isSubmitting } = formState;

  const onSubmit = (data: Section2Form) => {
    updateDiscoveryState(data);
    void navigate({ to: "/discovery/refine/logistics" });
  };

  const handleBack = () => {
    void navigate({ to: "/discovery/refine" });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title="Personal Fit" />

      {/* Coach gender preference */}
      <DiscoveryStep.Card>
        <FormField
          label={
            coachGenderField.subTitle ||
            "Do you have a coach gender preference?"
          }
          labelSize="xl"
          error={errors.coachGender?.message}
        >
          <Controller
            control={control}
            name="coachGender"
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex flex-col gap-y-4"
              >
                {coachGenderField.options?.map(({ value, label }) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-3"
                  >
                    <RadioGroupItem value={value} />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </RadioGroup>
            )}
          />
        </FormField>
      </DiscoveryStep.Card>

      {/* Coach age preference */}
      <DiscoveryStep.Card>
        <FormField
          label={coachAgesField.subTitle || "Coach age preference"}
          labelSize="xl"
          error={errors.coachAges?.message}
        >
          <CheckboxGroupField
            name="coachAges"
            control={control}
            options={coachAgesField.options || []}
            exclusiveOptions={["No preference"]}
          />
        </FormField>
      </DiscoveryStep.Card>

      {/* Your personality */}
      <DiscoveryStep.Card>
        <FormField
          label={
            userPersonalityField.subTitle || "How would you describe yourself?"
          }
          labelSize="xl"
          error={errors.userPersonality?.message}
        >
          <CheckboxGroupField
            name="userPersonality"
            control={control}
            options={userPersonalityField.options || []}
            exclusiveOptions={["Skip this question"]}
          />
        </FormField>
      </DiscoveryStep.Card>

      {/* Coach personality */}
      <DiscoveryStep.Card>
        <FormField
          label={
            coachPersonalityField.subTitle ||
            "What coaching personality do you prefer?"
          }
          labelSize="xl"
          error={errors.coachPersonality?.message}
        >
          <CheckboxGroupField
            name="coachPersonality"
            control={control}
            options={coachPersonalityField.options || []}
            exclusiveOptions={["Skip this question"]}
          />
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control
        prev={handleBack}
        type="submit"
        loading={isSubmitting}
      />
    </form>
  );
}
